import React from 'react';
import { Globe2, Loader2 } from 'lucide-react';
import { BriefCard } from './BriefCard';
import { SummaryStrip, UniverseTable } from './UniverseTable';
import { WebResults } from './WebResults';
import { ContactsList } from './ResearchPanel';

const ContactsByCompany = ({ contacts, navigate }) => {
  const groups = {};
  (contacts || []).forEach(c => { (groups[c.company_name] = groups[c.company_name] || { ...c, list: [] }).list.push(c); });
  const names = Object.keys(groups).sort();
  if (!names.length) return <p className="text-xs text-slate-500">No contacts on file — use <b>Find BD Contacts</b> on a company row to search the web.</p>;
  return (
    <div className="space-y-2" data-testid="contacts-by-company">
      {names.map(n => {
        const g = groups[n];
        const path = g.kind === 'pharma' ? `/company-profiles/${g.company_id}` : `/biotech/${g.company_id}`;
        return (
          <div key={n} className="rounded-md border border-slate-200 bg-white p-2">
            <button onClick={() => navigate(path)} className="text-xs font-semibold text-slate-900 hover:text-sky-700 hover:underline">{n}</button>
            <span className="ml-2 text-[10px] text-slate-400">{g.tier} relevance · {g.list.length} contact{g.list.length > 1 ? 's' : ''}</span>
            <ContactsList contacts={g.list} />
          </div>
        );
      })}
    </div>
  );
};

export const DiscoveryMessage = ({ m, isLatest, disco, handlers, navigate }) => {
  const d = m.discovery;
  if (m.discovery_status === 'running') {
    return (
      <div className="rounded-lg border border-sky-200 bg-sky-50/50 p-3 flex items-center gap-2 text-xs text-slate-700" data-testid="discovery-running">
        <Loader2 className="w-3.5 h-3.5 animate-spin text-sky-600" />
        <span>{disco.jobStep?.[m.discovery_job_id] || 'Interpreting requirement…'}</span>
        <span className="ml-auto text-[10px] text-slate-400">{disco.jobProgress?.[m.discovery_job_id] ?? 0}%</span>
      </div>
    );
  }
  if (!d) return null;
  const showBrief = m.tool === 'discover_universe';
  return (
    <div className="space-y-2 mt-2" data-testid="discovery-message">
      {showBrief && <BriefCard brief={d.brief} compact />}
      {d.summary && <SummaryStrip summary={d.summary} scanned={d.candidates_scanned} refinement={d.refinement} />}
      {d.kind === 'contacts' ? (
        <ContactsByCompany contacts={d.contacts} navigate={navigate} />
      ) : (
        <UniverseTable
          results={d.results}
          selected={disco.selected}
          onToggle={handlers.toggle}
          onSelectMany={handlers.selectMany}
          navigate={navigate}
          research={disco.research}
          researching={disco.researching}
          onResearch={handlers.research}
          crmContacts={disco.crmContacts}
          onLoadContacts={handlers.loadContacts}
          onExport={handlers.exportExcel}
          exporting={disco.exporting}
        />
      )}
      {isLatest && (
        <div className="space-y-2">
          {!disco.webLoading && (!disco.webResults || disco.webResults.length === 0) && (
            <button onClick={handlers.expandWeb} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border border-emerald-600 text-emerald-700 bg-white hover:bg-emerald-50" data-testid="expand-web-btn">
              <Globe2 className="w-3.5 h-3.5" /> Expand Search — search web for additional companies not in ForesightRx
            </button>
          )}
          <WebResults rows={disco.webResults} loading={disco.webLoading} selected={disco.selected} onToggle={handlers.toggle} onAddCompany={handlers.addCompany} adding={disco.adding} navigate={navigate} />
          {!disco.webLoading && disco.webResults?.length > 0 && (
            <button onClick={handlers.expandWeb} className="text-[10px] text-emerald-700 hover:underline" data-testid="expand-web-again">Search the web again</button>
          )}
        </div>
      )}
    </div>
  );
};
